import { Component } from '@theme/component';
import { onAnimationEnd } from '@theme/utilities';
import { ThemeEvents } from '@theme/events';

/**
 * A custom element that displays a cart icon.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} cartBubble - The cart bubble element.
 * @property {HTMLElement} cartBubbleText - The cart bubble text element.
 * @property {HTMLElement} cartBubbleCount - The cart bubble count element.
 *
 * @extends {Component<Refs>}
 */
class CartIcon extends Component {
  requiredRefs = ['cartBubble', 'cartBubbleText', 'cartBubbleCount'];
  syncVersion = 0;

  get currentCartCount() {
    return parseInt(this.refs.cartBubbleCount.textContent ?? '0', 10) || 0;
  }

  set currentCartCount(value) {
    this.refs.cartBubbleCount.textContent = value < 100 ? String(value) : '';

    const hiddenLabel = this.refs.cartBubbleText.querySelector('.visually-hidden');
    if (hiddenLabel) {
      hiddenLabel.textContent = hiddenLabel.textContent.replace(/\d+\s*$/, String(value));
    }
  }

  connectedCallback() {
    super.connectedCallback();
    document.addEventListener(ThemeEvents.cartUpdate, this.onCartUpdate);
    document.addEventListener('click', this.onCartControlClick);
    window.addEventListener('pageshow', this.onPageShow);
    this.syncCartBubble(false);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(ThemeEvents.cartUpdate, this.onCartUpdate);
    document.removeEventListener('click', this.onCartControlClick);
    window.removeEventListener('pageshow', this.onPageShow);
  }

  onPageShow = () => {
    this.syncCartBubble(false);
  };

  onCartUpdate = (event) => {
    const eventCount = event.detail?.data?.itemCount;

    if (eventCount != null) {
      const itemCount = Number(eventCount) || 0;
      const comingFromProductForm = event.detail.data?.source === 'product-form-component';
      this.renderCartBubble(itemCount, comingFromProductForm, true);
    }

    // Reconcile shortly afterwards with Shopify's authoritative cart. The
    // timestamp bypasses cached /cart.js responses seen after quick changes.
    this.syncCartBubble(false, 200);
  };

  onCartControlClick = (event) => {
    const control = event.target.closest?.(
      '.cart-drawer button[name="plus"], .cart-drawer button[name="minus"], cart-drawer button[name="plus"], cart-drawer button[name="minus"]'
    );

    if (!control) return;

    const delta = control.name === 'plus' ? 1 : -1;
    this.renderCartBubble(Math.max(0, this.currentCartCount + delta), false, true);
    this.syncCartBubble(false, 600);
  };

  /**
   * Fetches the authoritative cart quantity so the header never lags behind
   * product forms, the cart drawer, browser history, or another tab.
   *
   * @param {boolean} animate
   * @param {number} delay
   */
  syncCartBubble = async (animate, delay = 0) => {
    const requestVersion = ++this.syncVersion;

    if (delay > 0) {
      await new Promise((resolve) => setTimeout(resolve, delay));
    }

    try {
      const response = await fetch('/cart.js?cart_refresh=' + Date.now(), {
        headers: { Accept: 'application/json' },
        cache: 'no-store',
      });

      if (!response.ok) throw new Error('Unable to refresh cart quantity');

      const cart = await response.json();
      if (requestVersion !== this.syncVersion) return;

      await this.renderCartBubble(Number(cart.item_count) || 0, false, animate);
    } catch (_) {
      // Keep the server-rendered or optimistic event count if reconciliation fails.
    }
  };

  /**
   * Updates the cart icon bubble.
   *
   * @param {number} itemCount
   * @param {boolean} comingFromProductForm
   * @param {boolean} animate
   */
  renderCartBubble = async (itemCount, comingFromProductForm, animate = true) => {
    this.refs.cartBubbleCount.classList.toggle('hidden', itemCount === 0);
    this.refs.cartBubble.classList.toggle('visually-hidden', itemCount === 0);
    this.currentCartCount = comingFromProductForm ? this.currentCartCount + itemCount : itemCount;
    this.classList.toggle('header-actions__cart-icon--has-cart', itemCount > 0);

    sessionStorage.setItem(
      'cart-count',
      JSON.stringify({ value: String(this.currentCartCount), timestamp: Date.now() })
    );

    if (!animate || itemCount === 0) return;

    await new Promise((resolve) => requestAnimationFrame(resolve));
    this.refs.cartBubble.classList.add('cart-bubble--animating');
    await onAnimationEnd(this.refs.cartBubbleText);
    this.refs.cartBubble.classList.remove('cart-bubble--animating');
  };
}

if (!customElements.get('cart-icon')) {
  customElements.define('cart-icon', CartIcon);
}
